import { isBlankContentValue, isUnreachableFieldPath } from './internal';
import { walkTranslatableUnits } from './walk';
import {
  H5PParams,
  H5PSemantics,
  ProgressCount,
  TranslatableField,
  TranslationMap,
  TranslationProgress,
  TRANSLATABLE_FIELD_TYPES,
  WalkerOptions,
} from './types';

/**
 * Count translation progress of a content against a translation map. Only
 * non-blank, editor-reachable sources count; `perItem` overlaps by ancestry.
 */
export const countProgress = (
  semantics: H5PSemantics,
  params: H5PParams,
  map: TranslationMap | undefined | null,
  options?: WalkerOptions,
): TranslationProgress => {
  const { fields, excludedItems } = walkTranslatableUnits(
    semantics,
    params,
    options,
  );
  const perItem: Record<string, { translated: number; total: number }> = {};
  let translated = 0;
  let total = 0;

  const bump = (key: string, isTranslated: boolean): void => {
    const entry = (perItem[key] = perItem[key] ?? { translated: 0, total: 0 });
    entry.total++;
    if (isTranslated) {
      entry.translated++;
    }
  };

  for (const field of fields) {
    if (!countsTowardTotal(field)) {
      continue;
    }
    const isTranslated = isFieldTranslated(field, map);
    total++;
    if (isTranslated) {
      translated++;
    }
    for (const key of field.keyChain) {
      bump(key, isTranslated);
    }
  }

  for (const excluded of excludedItems) {
    perItem[excluded.itemKey] = perItem[excluded.itemKey] ?? {
      translated: 0,
      total: 0,
    };
  }

  return { translated, total, perItem };
};

/**
 * Whether a field counts toward the total at all — the source-non-blank and
 * editor-reachable rules, single-sourced so every counting surface agrees.
 */
const countsTowardTotal = (field: TranslatableField): boolean =>
  !isBlankContentValue(field.sourceValue) &&
  !isUnreachableFieldPath(field.pathSegments) &&
  // Colours always fall back to the source colour, so they are translatable
  // but never REQUIRED — they must not gate a language's completeness.
  field.widget !== 'colorSelector';

/**
 * The module-sum half of {@link countProgress} over ALREADY extracted fields,
 * so scoring N languages walks once. Shares its predicates — no drift.
 */
export const countExtractedFields = (
  fields: readonly TranslatableField[],
  map: TranslationMap | undefined | null,
): ProgressCount => {
  let translated = 0;
  let total = 0;
  for (const field of fields) {
    if (!countsTowardTotal(field)) {
      continue;
    }
    total++;
    if (isFieldTranslated(field, map)) {
      translated++;
    }
  }
  return { translated, total };
};

const isFieldTranslated = (
  field: TranslatableField,
  map: TranslationMap | undefined | null,
): boolean => {
  if (!map || field.mapKey === null) {
    return false;
  }
  const value = map[field.mapKey];
  if (isBlankContentValue(value)) {
    return false;
  }
  const expectsString =
    field.type === TRANSLATABLE_FIELD_TYPES.TEXT ||
    field.type === TRANSLATABLE_FIELD_TYPES.HTML;
  return expectsString ? typeof value === 'string' : typeof value !== 'string';
};

/** Convenience: percentage helper for UI chips (0-100, 100 when 0/0). */
export const progressPercentage = (count: ProgressCount): number =>
  count.total === 0 ? 100 : Math.round((count.translated / count.total) * 100);
